/**
 * CFS back-in-stock form (snippets/cfs-restock-form.liquid; owner, 24 Oct 2026).
 * Sends the sign-up to the cfs-whatsapp app through Shopify's app proxy. A sign-up swaps the form
 * for a "You're on the list" card with the app's reply (its wording comes from the app's Settings);
 * errors show under the form. A sign-up is remembered on this device per variant, so coming back
 * shows the card straight away. Nothing is sent to WhatsApp at sign-up.
 * An AI agent using the form as a WebMCP tool gets the app's reply as the tool result.
 */

const STORE_KEY = 'cfs:restock';

/** @returns {Record<string, string>} variant id → number or email signed up with */
function remembered() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** @param {string} variantId @param {string | null} contact (null forgets it) */
function remember(variantId, contact) {
  try {
    const all = remembered();
    if (contact) all[variantId] = contact;
    else delete all[variantId];
    localStorage.setItem(STORE_KEY, JSON.stringify(all));
  } catch {
    // Private mode: nothing to remember.
  }
}

/** @param {HTMLElement} root */
function setUp(root) {
  const form = /** @type {HTMLFormElement | null} */ (root.querySelector('form[data-cfs-restock-form]'));
  if (!form || root.dataset.cfsReady) return;
  root.dataset.cfsReady = '1';
  const status = /** @type {HTMLElement} */ (form.querySelector('[data-cfs-restock-status]'));
  const submit = /** @type {HTMLButtonElement} */ (form.querySelector('[data-cfs-restock-submit]'));
  const variantInput = /** @type {HTMLInputElement} */ (form.querySelector('[data-cfs-restock-variant]'));
  const done = /** @type {HTMLElement} */ (root.querySelector('[data-cfs-restock-done]'));
  const doneText = /** @type {HTMLElement} */ (root.querySelector('[data-cfs-restock-done-text]'));
  const phone = /** @type {HTMLInputElement} */ (form.elements.namedItem('phone'));
  const email = /** @type {HTMLInputElement} */ (form.elements.namedItem('email'));
  // The variant chosen in the product form (options with several sizes), when there is one.
  const productSelect = /** @type {HTMLSelectElement | null} */ (
    root.closest('.cfs-product')?.querySelector('select[data-cfs-variant-input]') ?? null
  );

  /** @param {string} text @param {'err' | ''} kind */
  const showStatus = (text, kind = '') => {
    status.textContent = text;
    status.dataset.kind = kind;
  };

  /** @param {string} text @param {boolean} [focus] */
  const showDone = (text, focus = false) => {
    doneText.textContent = text;
    form.hidden = true;
    done.hidden = false;
    if (focus) done.focus();
  };

  const onList = remembered()[variantInput.value];
  if (onList) showDone(`We'll message ${onList} when it's back.`);

  // From a sold-out card's "Notify me when it's back" (#back-in-stock): bring the form into view, clear
  // of the sticky header, and put the cursor in the number field.
  if (location.hash === `#${root.id}`) {
    requestAnimationFrame(() => {
      root.scrollIntoView({ block: 'start' }); // scroll-margin-top in cfs-product.css clears the header
      if (!form.hidden) phone.focus({ preventScroll: true });
    });
  }

  root.querySelector('[data-cfs-restock-change]')?.addEventListener('click', () => {
    remember(variantInput.value, null);
    done.hidden = true;
    form.hidden = false;
    showStatus('');
    phone.focus();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    // A WhatsApp number, an email, or both (email-only sign-ups: owner, 24 Oct 2026)
    if (!phone.value.trim() && !email.value.trim()) {
      showStatus('Enter your WhatsApp number, or an email.', 'err');
      phone.focus();
      /** @type {any} */ (event).respondWith?.(Promise.resolve('Enter a WhatsApp number or an email.'));
      return;
    }
    if (productSelect?.value) variantInput.value = productSelect.value;
    const sourceInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cfs-restock-source]'));
    if (sourceInput) sourceInput.value = /** @type {any} */ (event).agentInvoked ? 'agent' : 'theme';

    const job = (async () => {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      showStatus('');
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new URLSearchParams(/** @type {any} */ (new FormData(form))),
        });
        const body = await res.json().catch(() => ({}));
        const message = body.message || 'Sorry, something went wrong. Please try again.';
        if (body.ok) {
          remember(variantInput.value, phone.value.trim() || email.value.trim());
          showDone(message, true);
        } else {
          showStatus(message, 'err');
        }
        return message;
      } catch {
        const message = "Sorry, we couldn't save that. Please check your connection and try again.";
        showStatus(message, 'err');
        return message;
      } finally {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    })();
    // WebMCP: the agent gets the reply shown to the shopper.
    /** @type {any} */ (event).respondWith?.(job);
  });
}

document.querySelectorAll('[data-cfs-restock]').forEach((root) => setUp(/** @type {HTMLElement} */ (root)));
